import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button, Checkbox } from 'antd-v4'
import { Icon, message } from 'antd'
import { THEME_COLORS } from '@getgreenline/shared'
import {
  applyOrgUserMigration,
  getOrgUserMigrationPreview,
  IOrgUserMigrationUser,
} from '../../Dashboard/DashboardEmployees/orgUserMigrationApi'
import {
  buildApplyPayload,
  describeCompanies,
  selectAllUsers,
  toggleUserSelection,
} from '../../Dashboard/DashboardEmployees/orgUserMigrationHandlers'
import { parseErrorMsg } from '../../../utilities/helpers'

interface Props {
  organizationId: number
}

const UserRow = ({
  user,
  checked,
  onChange,
}: {
  user: IOrgUserMigrationUser
  checked: boolean
  onChange: (checked: boolean) => void
}) => (
  <div style={styles.userRow}>
    <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <div>
      <div style={styles.userName}>{user.name}</div>
      <div style={styles.userMeta}>
        {user.email ?? 'No email'} · {describeCompanies(user)}
      </div>
    </div>
  </div>
)

export const OrgUserMigrationSection = ({ organizationId }: Props) => {
  const [candidates, setCandidates] = useState<IOrgUserMigrationUser[]>([])
  const [alreadyAssignedCount, setAlreadyAssignedCount] = useState(0)
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const loadPreview = async () => {
    setIsLoading(true)
    try {
      const preview = await getOrgUserMigrationPreview(organizationId, 'admin')
      if (!isMountedRef.current) return
      setCandidates(preview.candidates)
      setAlreadyAssignedCount(preview.alreadyAssigned.length)
      setSelectedUserIds(selectAllUsers(preview.candidates, true))
    } catch (error) {
      if (isMountedRef.current) {
        message.error(parseErrorMsg(error) || 'Failed to load organization users')
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPreview()
  }, [organizationId])

  const assignUsers = async () => {
    setIsSaving(true)
    try {
      const result = await applyOrgUserMigration(
        organizationId,
        buildApplyPayload(candidates, selectedUserIds),
        'admin',
      )
      message.success(
        `${result.migrated} user${result.migrated === 1 ? '' : 's'} assigned to the organization`,
      )
      await loadPreview()
    } catch (error) {
      if (isMountedRef.current) {
        message.error(parseErrorMsg(error) || 'Failed to assign users to the organization')
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false)
    }
  }

  const allSelected = candidates.length > 0 && selectedUserIds.size === candidates.length

  return (
    <div className='mb-3'>
      <div style={styles.header}>
        <span style={styles.headerTitle}>Users</span>
        <span style={styles.headerCount}>
          {candidates.length} unassigned · {alreadyAssignedCount} assigned
        </span>
      </div>

      {isLoading && <Icon type='loading' />}

      {!isLoading && candidates.length === 0 && (
        <div style={styles.emptyText}>Every user of this organization is assigned to it.</div>
      )}

      {!isLoading && candidates.length > 0 && (
        <>
          <div style={styles.selectAllRow}>
            <Checkbox
              checked={allSelected}
              onChange={(e) => setSelectedUserIds(selectAllUsers(candidates, e.target.checked))}
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </Checkbox>
          </div>

          {candidates.map((user) => (
            <UserRow
              key={user.userId}
              user={user}
              checked={selectedUserIds.has(user.userId)}
              onChange={(checked) =>
                setSelectedUserIds((previous) =>
                  toggleUserSelection(previous, user.userId, checked),
                )
              }
            />
          ))}

          <Button
            type='primary'
            loading={isSaving}
            disabled={selectedUserIds.size === 0}
            onClick={assignUsers}
          >
            Assign to organization
          </Button>
        </>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerTitle: {
    fontWeight: 600,
  },
  headerCount: {
    color: THEME_COLORS.MONO.mono77,
    fontSize: 13,
  },
  emptyText: {
    color: THEME_COLORS.MONO.mono77,
    fontSize: 13,
  },
  selectAllRow: {
    marginBottom: 8,
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: `1px solid ${THEME_COLORS.MONO.monoee}`,
    borderRadius: 4,
    marginBottom: 8,
    background: THEME_COLORS.MONO.monoFa,
  },
  userName: {
    fontWeight: 500,
  },
  userMeta: {
    fontSize: 12,
    color: THEME_COLORS.MONO.mono77,
  },
}
